import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ApiService } from '../../../../shared/service/Apiservice';
import { environment } from '../../../../shared/enviroment/environment';
import buildCoinsService from '../../../Profile/service/BuildCoins.service';
import { ProfileService } from '../../../Profile/service/Profile.service';
import { useCart } from '../../../../contexts/CartContext';
import './styles/OrderSummary.css';

interface CartItem {
  id?: string | number;
  productId?: string | number;
  _id?: string | number;
  name?: string;
  title?: string;
  price: number;
  quantity: number;
  img?: string;
  imageUrl?: string;
  variantSku?: string;
  sku?: string;
  variant?: {
    sku?: string;
  };
}

interface OrderSummaryProps {
  items?: CartItem[];
  subTotal?: number;
  delivery?: number;
  grandTotal?: number;
  onQuantityChange?: (itemId: string | number, newQuantity: number) => void;
  onFinalTotalChange?: (total: number) => void;
  onOrderPlaced?: () => void;
  onItemRemoved?: () => void; // Add callback for when an item is removed
  selectedPayment?: 'online' | 'cod' | null;
  selectedDelivery?: 'immediate' | 'schedule' | null;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ 
  items, 
  subTotal, 
  delivery, 
  onQuantityChange,
  onFinalTotalChange, 
  onOrderPlaced,
  onItemRemoved,
  selectedPayment,
  selectedDelivery 
}) => {
  const [localQuantities, setLocalQuantities] = useState<Record<string | number, number>>({});
  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  
  // Build Coins state
  const [buildCoinsBalance, setBuildCoinsBalance] = useState(0);
  const [buildCoinsToUse, setBuildCoinsToUse] = useState(0);
  const [maxBuildCoins, setMaxBuildCoins] = useState(0);
  const [buildCoinsLoading, setBuildCoinsLoading] = useState(false);

  
  
  const { removeFromCart, fetchCartFromBackend } = useCart();
  
  const handleRemoveItem = (id: string | number | undefined, variantSku?: string, variantId?: string) => {
    
    if (id === undefined || id === null) {      return;
    }
    
    const itemId = String(id);
    if (!itemId || itemId === '0') {
      return;
    }
        
    const removeItem = async () => {
      try {
        await removeFromCart(itemId, variantSku, variantId);
        
        try {
          await fetchCartFromBackend();
        } catch (fetchError) {
        }
        
        onItemRemoved?.();
      } catch (error) {
      }
    };
    
    removeItem();
  };
  
  const handleQuantityChange = (itemId: string | number, newQuantity: number) => {
    if (newQuantity >= 1) {
      setLocalQuantities(prev => ({
        ...prev,
        [itemId]: newQuantity
      }));
      onQuantityChange?.(itemId, newQuantity);
    }
  };

  const formatPrice = (price: number) => {
    return Math.round(price).toString();
  };

  const getDeliveryTime = () => {
    const now = new Date();
    const deliveryTime = new Date(now.getTime() + 30 * 60 * 1000);
    return deliveryTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  };

  const fallbackItems: CartItem[] = [
    { 
      id: 1, 
      name: 'Ultra Tech Cement 50kg (1 pack)', 
      price: 1799, 
      quantity: 1, 
      img: '/images/logo.png', 
      imageUrl: '/images/logo.png' 
    },
    { 
      id: 2, 
      name: 'Ultra Tech Cement 50kg (1 pack)', 
      price: 1799, 
      quantity: 1, 
      img: '/images/logo.png', 
      imageUrl: '/images/logo.png' 
    },
  ];
  const displayItems = items && items.length > 0 ? items : fallbackItems;
  
  const calculateSubTotal = () => {
    return displayItems.reduce((total, item, idx) => {
      const itemId = item.id || idx;
      const currentQty = localQuantities[itemId] || item.quantity;
      return total + (item.price * currentQty);
    }, 0);
  };

  const dynamicSubTotal = calculateSubTotal();
  const displaySubTotal = typeof subTotal === 'number' && Object.keys(localQuantities).length === 0 ? subTotal : dynamicSubTotal;
  const displayDelivery = typeof delivery === 'number' ? delivery : 300;
  const subtotalBeforeDiscount = displaySubTotal + displayDelivery;
  const displayGrandTotal = subtotalBeforeDiscount - discount - buildCoinsToUse;

  // Notify parent whenever the final total changes
  useEffect(() => {
    onFinalTotalChange?.(displayGrandTotal);
  }, [displayGrandTotal, onFinalTotalChange]);

  const handleApplyCoupon = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }

    setPromoLoading(true);
    setPromoError('');

    try {
      const apiService = new ApiService(axios.create());
      const response: any = await apiService.postRequest(
        'checkout/apply-coupon',
        {
          code: promoCode.trim(),
          orderTotal: subtotalBeforeDiscount,
        },
        {
          apiRoot: environment.api.apiRootServer,
        }
      );

      if (response.success) {
        setDiscount(response.discount || 0);
        setAppliedCoupon(response.coupon?.code || promoCode);
        setPromoError('');
      } else {
        setPromoError(response.message || 'Invalid promo code');
        setDiscount(0);
        setAppliedCoupon(null);
      }
    } catch (error: any) {
      setPromoError(error.response?.data?.message || 'Failed to apply promo code');
      setDiscount(0);
      setAppliedCoupon(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setPromoCode('');
    setDiscount(0);
    setAppliedCoupon(null);
    setPromoError('');
  };

  // Fetch build coins balance on mount from user profile
  useEffect(() => {
    const fetchBuildCoins = async () => {
      try {
        const apiService = new ApiService(axios.create());
        const profileService = new ProfileService(apiService);
        const response: any = await profileService.getUserProfile();
        if (response.success && response.user) {
          setBuildCoinsBalance(response.user.referralCredits || 0);
        }
      } catch (error) {
      }
    };
    fetchBuildCoins();
  }, []);

  // Update max build coins when order total changes
  useEffect(() => {
    const orderTotal = subtotalBeforeDiscount - discount;
    const maxCoins = Math.min(buildCoinsBalance, orderTotal * 0.5);
    setMaxBuildCoins(maxCoins);
  }, [subtotalBeforeDiscount, discount, buildCoinsBalance]);

  const handleUseAllBuildCoins = async () => {
    if (buildCoinsBalance <= 0) {
      return;
    }

    setBuildCoinsLoading(true);
    try {
      const orderTotal = subtotalBeforeDiscount - discount;
      const response: any = await buildCoinsService.getAvailableCoinsForPurchase(orderTotal);
      
      if (response.success) {
        const coinsToUse = response.data.availableCoins;
        setBuildCoinsToUse(coinsToUse);
      }
    } catch (error) {
    } finally {
      setBuildCoinsLoading(false);
    }
  };

  const handleRemoveBuildCoins = () => {
    setBuildCoinsToUse(0);
  };

  return (
    <div className="card order-summary-card">
      <div className="card-header">
        <div className="card-title">Order Summary</div>
      </div>
      <div className="card-body">
        <div className="cart-items-scroll-container">
          <div className="cart-items">
            {displayItems.map((it, idx) => {
              const imageSrc = it.img || (it as any).imageUrl;
              const title = it.name || '';
              const firstWord = title.split(' ')[0];
              const rest = title.substring(firstWord.length).trim();
              // Safely get product ID and variant SKU with proper type checking
              const productId = it.id || it.productId || it._id || idx;
              const variantSku = 
                (it as any).variantSku || 
                (it as any).sku || 
                ((it as any).variant ? (it as any).variant.sku : undefined);
              const variantId = (it as any).variantId || (it as any).variant?.id;
              const currentQty = localQuantities[productId] || it.quantity;
              const itemTotal = it.price * currentQty;
              
              return (
                <div className="cart-item" key={`${productId}-${variantSku || 'default'}`}>
                  <div className="item-image-container">
                    {imageSrc && (
                      <img
                        className="item-img"
                        src={imageSrc}
                        alt={it.name || 'product'}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/images/placeholder-product.png';
                        }}
                      />
                    )}
                    <button 
                      className="remove-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveItem(productId, variantSku, variantId);
                      }}
                      aria-label="Remove item"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="item-info">
                    <div className="item-title" style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '180px',
                      fontWeight: 500
                    }}>{rest}</div>
                    <div className="item-title" style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '180px',
                      whiteSpace: 'nowrap'
                    }}>{firstWord}...</div>
                    <div className="quantity-counter" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '8px'
                    }}>
                      <span style={{ fontSize: '12px', color: '#666' }}>Qty:</span>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#fff'
                      }}>
                        <button
                          onClick={() => handleQuantityChange(it.id || idx, (localQuantities[it.id || idx] || it.quantity) - 1)}
                          disabled={(localQuantities[it.id || idx] || it.quantity) <= 1}
                          style={{
                            border: 'none',
                            background: 'none',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: (localQuantities[it.id || idx] || it.quantity) <= 1 ? '#ccc' : '#666'
                          }}
                        >
                          −
                        </button>
                        <span style={{
                          padding: '4px 8px',
                          minWidth: '30px',
                          textAlign: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          borderLeft: '1px solid #ddd',
                          borderRight: '1px solid #ddd'
                        }}>
                          {localQuantities[it.id || idx] || it.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(it.id || idx, (localQuantities[it.id || idx] || it.quantity) + 1)}
                          style={{
                            border: 'none',
                            background: 'none',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#666'
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="item-price">₹ {formatPrice(itemTotal)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="summary-row"><span>Sub Total</span><span>₹ {formatPrice(displaySubTotal)}</span></div>
        <div className="summary-row"><span>Delivery</span><span>₹ {formatPrice(displayDelivery)}</span></div>
        {discount > 0 && (
          <div className="summary-row" style={{ color: '#28a745' }}>
            <span>Discount ({appliedCoupon})</span>
            <span>- ₹ {formatPrice(discount)}</span>
          </div>
        )}
        {buildCoinsToUse > 0 && (
          <div className="summary-row" style={{ color: '#ff6b35' }}>
            <span>Build Coins Used</span>
            <span>- ₹ {formatPrice(buildCoinsToUse)}</span>
          </div>
        )}
        <div className="separator" />
        <div className="summary-row delivered"><span className="label">Delivered By</span><span className="info">Today, {getDeliveryTime()}</span></div>
        <div className="summary-row total"><span>Grand Total</span><span>₹ {formatPrice(displayGrandTotal)}</span></div>

        <div className="promo">
          <label>Promocode</label>
          {appliedCoupon ? (
            <div className="promo-row" style={{ alignItems: 'center' }}>
              <span style={{ color: '#28a745', fontSize: '14px', fontWeight: '500' }}>
                ✓ {appliedCoupon} applied
              </span>
              <button 
                className="apply-btn" 
                onClick={handleRemoveCoupon}
                style={{ background: '#dc3545' }}
              >
                Remove
              </button>
            </div>
          ) : (
            <>
              <div className="promo-row">
                <input 
                  className="promo-input" 
                  placeholder="Enter Code" 
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                />
                <button 
                  className="apply-btn" 
                  onClick={handleApplyCoupon}
                  disabled={promoLoading}
                >
                  {promoLoading ? 'Applying...' : 'Apply'}
                </button>
              </div>
              {promoError && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {promoError}
                </div>
              )}
            </>
          )}
        </div>

        <button 
          className="pay-btn" 
          style={{ 
            margin: '24px 0',
            opacity: (!selectedPayment || !selectedDelivery) ? 0.5 : 1,
            cursor: (!selectedPayment || !selectedDelivery) ? 'not-allowed' : 'pointer'
          }}
          onClick={() => {
            if (selectedPayment && selectedDelivery) {
              // Call the callback to save address after order is placed
              onOrderPlaced?.();
              // Add your order placement logic here
            }
          }}
          disabled={!selectedPayment || !selectedDelivery}
        >
          Pay Securely ₹ {formatPrice(displayGrandTotal)}
        </button>

        <div className="build-coins">
          <div className="build-coins-header">
            <span className="title">Build Coins</span>
            <span className="available">Available: {formatPrice(buildCoinsBalance)} coins</span>
          </div>
          <div className="build-coins-note">
            Use your Build Coins to get up to 50% off your order
            {maxBuildCoins > 0 && ` (Max: ₹${formatPrice(maxBuildCoins)})`}
          </div>
          {buildCoinsToUse > 0 ? (
            <button 
              className="build-coins-btn" 
              type="button"
              onClick={handleRemoveBuildCoins}
              style={{ background: '#dc3545' }}
            >
              Remove Build Coins (₹{formatPrice(buildCoinsToUse)})
            </button>
          ) : (
            <button 
              className="build-coins-btn" 
              type="button"
              onClick={handleUseAllBuildCoins}
              disabled={buildCoinsLoading || buildCoinsBalance <= 0 || maxBuildCoins <= 0}
            >
              {buildCoinsLoading ? 'Applying...' : 'Use All Build Coins'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;


